/**
 * Simple Timeline - Month-by-month view of restoration progress
 *
 * Shows what was done each month with representative photos.
 * Designed to be scannable and useful, not fancy.
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import type { TimelineSegment, TimelineConfig, TemporalEvent, MemoryAnchor } from '@/types/memory';
import type { RestorePhase } from '@/types';

interface TimelineProps {
  segments: TimelineSegment[];
  events: TemporalEvent[];
  anchors?: MemoryAnchor[];
  config: TimelineConfig;
  onConfigChange: (config: TimelineConfig) => void;
  onSegmentClick: (segment: TimelineSegment) => void;
  onEventClick: (event: TemporalEvent) => void;
  onAnchorClick?: (anchor: MemoryAnchor) => void;
  className?: string;
}

// Phase display info
const PHASE_INFO: Record<RestorePhase, { label: string; color: string }> = {
  'initial-assessment': { label: 'Assessment', color: 'bg-indigo-500' },
  'teardown': { label: 'Teardown', color: 'bg-red-500' },
  'inventory': { label: 'Inventory', color: 'bg-amber-500' },
  'restoration': { label: 'Restoration', color: 'bg-emerald-500' },
  'assembly': { label: 'Assembly', color: 'bg-blue-500' },
  'finishing': { label: 'Finishing', color: 'bg-violet-500' },
  'quality-check': { label: 'QC', color: 'bg-pink-500' },
  'delivery': { label: 'Delivery', color: 'bg-teal-500' },
  'completed': { label: 'Completed', color: 'bg-green-500' },
};

interface MonthGroup {
  key: string;
  label: string;
  events: TemporalEvent[];
  phases: RestorePhase[];
  imageCount: number;
  anchors: MemoryAnchor[];
}

export function Timeline({
  events,
  anchors = [],
  onEventClick,
  onAnchorClick,
  className,
}: TimelineProps) {
  // Group events by month
  const monthGroups = useMemo(() => {
    const groups = new Map<string, MonthGroup>();

    events.forEach(event => {
      const date = new Date(event.startTime);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label,
          events: [],
          phases: [],
          imageCount: 0,
          anchors: [],
        });
      }

      const group = groups.get(key)!;
      group.events.push(event);
      group.imageCount += event.imageCount;
      if (event.phase && !group.phases.includes(event.phase)) {
        group.phases.push(event.phase);
      }
    });

    // Add anchors to their respective months
    anchors.forEach(anchor => {
      const date = new Date(anchor.timestamp);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const group = groups.get(key);
      if (group) {
        group.anchors.push(anchor);
      }
    });

    // Sort by date descending (most recent first)
    return Array.from(groups.values()).sort((a, b) => b.key.localeCompare(a.key));
  }, [events, anchors]);

  if (events.length === 0) {
    return (
      <div className={clsx('flex items-center justify-center h-48 bg-workshop-surface rounded-lg', className)}>
        <p className="text-workshop-text-muted">No timeline data available</p>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-6', className)}>
      {monthGroups.map((month, monthIndex) => (
        <motion.div
          key={month.key}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: monthIndex * 0.05 }}
          className="bg-workshop-surface rounded-lg overflow-hidden"
        >
          {/* Month header */}
          <div className="px-4 py-3 border-b border-workshop-border flex items-center justify-between">
            <h3 className="text-white font-semibold">{month.label}</h3>
            <div className="flex items-center gap-3 text-sm text-workshop-text-muted">
              <span>{month.events.length} events</span>
              <span>{month.imageCount} photos</span>
            </div>
          </div>

          {/* Phase tags */}
          {month.phases.length > 0 && (
            <div className="px-4 py-2 flex flex-wrap gap-2 border-b border-workshop-border/50">
              {month.phases.map(phase => {
                const info = PHASE_INFO[phase];
                return (
                  <span
                    key={phase}
                    className={clsx(
                      'px-2 py-0.5 rounded text-xs font-medium text-white',
                      info.color
                    )}
                  >
                    {info.label}
                  </span>
                );
              })}
            </div>
          )}

          {/* Milestones/Anchors for this month */}
          {month.anchors.length > 0 && (
            <div className="px-4 py-3 bg-workshop-accent/5 border-b border-workshop-border/50">
              <div className="flex flex-wrap gap-3">
                {month.anchors.map(anchor => (
                  <button
                    key={anchor.id}
                    onClick={() => onAnchorClick?.(anchor)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-workshop-accent/20 rounded-lg hover:bg-workshop-accent/30 transition-colors"
                  >
                    <span className="text-sm">
                      {anchor.anchorType === 'milestone' ? '🏁' :
                       anchor.anchorType === 'discovery' ? '🔍' :
                       anchor.anchorType === 'completion' ? '✅' : '📌'}
                    </span>
                    <span className="text-sm text-workshop-accent font-medium">{anchor.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Events list */}
          <div className="divide-y divide-workshop-border/30">
            {month.events.slice(0, 5).map((event) => (
              <button
                key={event.id}
                onClick={() => onEventClick(event)}
                className="w-full px-4 py-3 flex items-center gap-4 hover:bg-workshop-border/30 transition-colors text-left"
              >
                {/* Primary image thumbnail */}
                {event.primaryImageId ? (
                  <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0 bg-workshop-border">
                    <img
                      src={`/images/${event.primaryImageId}.jpg`}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded bg-workshop-border flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">
                      {event.type === 'inspection' ? '🔍' :
                       event.type === 'teardown' ? '🔧' :
                       event.type === 'fabrication' ? '🔨' :
                       event.type === 'repair' ? '🛠️' :
                       event.type === 'finishing' ? '🎨' :
                       event.type === 'installation' ? '📦' :
                       event.type === 'milestone' ? '🏁' :
                       event.type === 'discovery' ? '🔍' :
                       event.type === 'documentation' ? '📷' : '📷'}
                    </span>
                  </div>
                )}

                {/* Event info */}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{event.title}</p>
                  {event.description && (
                    <p className="text-sm text-workshop-text-muted truncate">{event.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-workshop-text-muted">
                    <span>
                      {new Date(event.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    {event.imageCount > 0 && (
                      <span>{event.imageCount} photos</span>
                    )}
                    {event.components.length > 0 && (
                      <span className="capitalize">{event.components[0]}</span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <ChevronIcon className="w-5 h-5 text-workshop-text-muted flex-shrink-0" />
              </button>
            ))}

            {/* Show more indicator */}
            {month.events.length > 5 && (
              <div className="px-4 py-2 text-center">
                <span className="text-sm text-workshop-text-muted">
                  +{month.events.length - 5} more events
                </span>
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/**
 * Compact timeline for embedding - simple horizontal bar
 */
export function TimelineMini({
  segments,
  onSegmentClick,
  className,
}: {
  segments: TimelineSegment[];
  onSegmentClick: (segment: TimelineSegment) => void;
  className?: string;
}) {
  if (segments.length === 0) return null;

  return (
    <div className={clsx('flex gap-1 h-8', className)}>
      {segments.map((segment) => {
        const phaseInfo = PHASE_INFO[segment.primaryPhase] || PHASE_INFO['initial-assessment'];

        return (
          <button
            key={segment.id}
            onClick={() => onSegmentClick(segment)}
            className={clsx(
              'flex-1 rounded transition-opacity hover:opacity-100',
              phaseInfo.color,
              segment.isGap ? 'opacity-10' : 'opacity-60'
            )}
            title={segment.label}
          />
        );
      })}
    </div>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

export default Timeline;
